import { useState, useEffect } from "react";
import axios from "axios";
import ExcelExportButton from "../components/ExcelExportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Box, Loader2, Download, FileText } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLASTIC_TYPES = ["PP", "PE", "ABS", "PA66", "POM", "PC", "PET", "PVC", "PS"];

const LOGO_URL = "https://i.imgur.com/8XmPJwK.png";

export default function Parts() {
  const [parts, setParts] = useState([]);
  const [molds, setMolds] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    unit_weight: "",
    plastic_type: "PP",
    product_type: "injected",
    stock_quantity: "0",
    min_stock_alert: "0",
    supplier: "",
    supplier_price: "",
    scrap_percentage: "5",
    client: "",
    quality_criteria: "",
    image_url: "",
    mold_id: "",
    machine_id: "",
    weight_min: "",
    weight_max: "",
    weight_tolerance_pct: "5",
    weight_10_pieces: "",
    units_per_bag: "250",
    empty_bag_weight: "15",
    bags_per_box: "12",
    boxes_per_pallet: "",
    box_dimensions: "",
  });

  const fetchParts = async () => {
    try {
      const [pRes, mRes, machRes] = await Promise.all([
        axios.get(`${API}/parts`),
        axios.get(`${API}/molds`),
        axios.get(`${API}/machines`),
      ]);
      setParts(pRes.data);
      setMolds(mRes.data);
      setMachines(machRes.data);
    } catch (error) {
      toast.error("Erro ao carregar peças");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParts();
  }, []);

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      unit_weight: "",
      plastic_type: "PP",
      product_type: "injected",
      stock_quantity: "0",
      min_stock_alert: "0",
      supplier: "",
      supplier_price: "",
      scrap_percentage: "5",
      client: "",
      quality_criteria: "",
      image_url: "",
      mold_id: "",
      machine_id: "",
      weight_min: "",
      weight_max: "",
      weight_tolerance_pct: "5",
      weight_10_pieces: "",
      units_per_bag: "250",
      empty_bag_weight: "15",
      bags_per_box: "12",
      boxes_per_pallet: "",
      box_dimensions: "",
    });
    setEditingPart(null);
  };

  const handleOpenDialog = (part = null) => {
    if (part) {
      setEditingPart(part);
      setFormData({
        code: part.code || "",
        description: part.description || "",
        unit_weight: (part.unit_weight ?? "").toString(),
        plastic_type: part.plastic_type || "PP",
        product_type: part.product_type || "injected",
        stock_quantity: (part.stock_quantity ?? 0).toString(),
        min_stock_alert: (part.min_stock_alert ?? 0).toString(),
        supplier: part.supplier || "",
        supplier_price: part.supplier_price != null ? part.supplier_price.toString() : "",
        scrap_percentage: ((part.scrap_percentage ?? 0.05) * 100).toString(),
        client: part.client || "",
        quality_criteria: Array.isArray(part.quality_criteria) ? part.quality_criteria.join(", ") : "",
        image_url: part.image_url || "",
        mold_id: part.mold_id || "",
        machine_id: part.machine_id || "",
        weight_min: part.weight_min != null ? part.weight_min.toString() : "",
        weight_max: part.weight_max != null ? part.weight_max.toString() : "",
        weight_tolerance_pct: part.weight_tolerance_pct != null ? part.weight_tolerance_pct.toString() : "5",
        weight_10_pieces: part.weight_10_pieces != null ? part.weight_10_pieces.toString() : "",
        units_per_bag: part.units_per_bag != null ? part.units_per_bag.toString() : "250",
        empty_bag_weight: part.empty_bag_weight != null ? part.empty_bag_weight.toString() : "15",
        bags_per_box: part.bags_per_box != null ? part.bags_per_box.toString() : "12",
        boxes_per_pallet: part.boxes_per_pallet != null ? part.boxes_per_pallet.toString() : "",
        box_dimensions: part.box_dimensions || "",
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      code: formData.code,
      description: formData.description,
      unit_weight: parseFloat(formData.unit_weight),
      plastic_type: formData.plastic_type,
      product_type: formData.product_type || "injected",
      stock_quantity: parseFloat(formData.stock_quantity) || 0,
      min_stock_alert: parseFloat(formData.min_stock_alert) || 0,
      supplier: formData.supplier || null,
      supplier_price: formData.supplier_price ? parseFloat(formData.supplier_price) : null,
      scrap_percentage: parseFloat(formData.scrap_percentage) / 100,
      client: formData.client,
      quality_criteria: formData.quality_criteria
        ? formData.quality_criteria.split(",").map((c) => c.trim())
        : [],
      image_url: formData.image_url || null,
      mold_id: formData.mold_id || null,
      machine_id: formData.machine_id || null,
      weight_min: formData.weight_min ? parseFloat(formData.weight_min) : null,
      weight_max: formData.weight_max ? parseFloat(formData.weight_max) : null,
      weight_tolerance_pct: parseFloat(formData.weight_tolerance_pct) || 5,
      weight_10_pieces: formData.weight_10_pieces ? parseFloat(formData.weight_10_pieces) : null,
      units_per_bag: parseInt(formData.units_per_bag) || 250,
      empty_bag_weight: parseFloat(formData.empty_bag_weight) || 15,
      bags_per_box: parseInt(formData.bags_per_box) || 12,
      boxes_per_pallet: parseInt(formData.boxes_per_pallet) || 0,
      box_dimensions: formData.box_dimensions || null,
    };

    try {
      if (editingPart) {
        await axios.put(`${API}/parts/${editingPart.id}`, payload);
        toast.success("Peça atualizada com sucesso");
      } else {
        await axios.post(`${API}/parts`, payload);
        toast.success("Peça criada com sucesso");
      }
      setDialogOpen(false);
      resetForm();
      fetchParts();
    } catch (error) {
      showError(error, "Erro ao guardar peça");
    }
  };

  const handleDelete = async (partId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta peça?")) return;
    
    try {
      await axios.delete(`${API}/parts/${partId}`);
      toast.success("Peça eliminada");
      fetchParts();
    } catch (error) {
      showError(error, "Erro ao eliminar peça");
    }
  };

  const exportToPDF = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Catálogo de Peças - EP3D Engineering</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; font-size: 10px; }
          .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 3px solid #4f46e5; padding-bottom: 15px; }
          .header-left { display: flex; align-items: center; gap: 15px; }
          .logo { height: 50px; }
          .header h1 { font-size: 20px; color: #4f46e5; }
          .header-right { text-align: right; }
          .header-right p { color: #64748b; font-size: 11px; }
          .summary { display: flex; gap: 20px; margin-bottom: 20px; }
          .summary-card { background: #f1f5f9; padding: 12px 20px; border-radius: 8px; text-align: center; }
          .summary-card .number { font-size: 24px; font-weight: bold; color: #4f46e5; }
          .summary-card .label { font-size: 10px; color: #64748b; }
          .parts-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
          .part-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; page-break-inside: avoid; }
          .part-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
          .part-code { font-size: 14px; font-weight: bold; color: #4f46e5; }
          .part-client { font-size: 9px; color: #64748b; background: #f1f5f9; padding: 2px 8px; border-radius: 4px; }
          .part-description { font-size: 12px; font-weight: 600; color: #1e293b; margin-bottom: 10px; }
          .part-details { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
          .detail-item { }
          .detail-label { font-size: 8px; color: #94a3b8; text-transform: uppercase; }
          .detail-value { font-size: 10px; font-weight: 500; color: #334155; }
          .weight-info { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #e2e8f0; }
          .weight-info h4 { font-size: 9px; color: #64748b; margin-bottom: 5px; }
          .quality-criteria { margin-top: 10px; padding: 8px; background: #fef3c7; border-radius: 4px; }
          .quality-criteria h4 { font-size: 8px; color: #92400e; margin-bottom: 3px; }
          .quality-criteria p { font-size: 9px; color: #78350f; }
          .footer { margin-top: 30px; text-align: center; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          @media print { 
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .part-card { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="header-left">
            <img src="${LOGO_URL}" alt="Logo" class="logo" />
            <div>
              <h1>Catálogo de Peças</h1>
              <p style="color: #64748b; font-size: 11px;">Lista completa de peças configuradas no sistema</p>
            </div>
          </div>
          <div class="header-right">
            <p><strong>Total de Peças:</strong> ${parts.length}</p>
            <p>Gerado: ${new Date().toLocaleString("pt-PT")}</p>
          </div>
        </div>
        
        <div class="summary">
          <div class="summary-card">
            <div class="number">${parts.length}</div>
            <div class="label">Total de Peças</div>
          </div>
          <div class="summary-card">
            <div class="number">${[...new Set(parts.map(p => p.client))].filter(Boolean).length}</div>
            <div class="label">Clientes</div>
          </div>
          <div class="summary-card">
            <div class="number">${[...new Set(parts.map(p => p.plastic_type || p.material))].filter(Boolean).length}</div>
            <div class="label">Tipos de Plástico</div>
          </div>
        </div>
        
        <div class="parts-grid">
          ${parts.map(part => `
            <div class="part-card">
              <div class="part-header">
                <div>
                  <div class="part-code">${part.code}</div>
                  <div class="part-description">${part.description || '-'}</div>
                </div>
                <div class="part-client">${part.client || 'N/A'}</div>
              </div>
              
              <div class="part-details">
                <div class="detail-item">
                  <div class="detail-label">Tipo de Plástico</div>
                  <div class="detail-value">${part.plastic_type || part.material || '-'}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Peso Unitário</div>
                  <div class="detail-value">${part.unit_weight ? part.unit_weight + ' g' : '-'}</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">% Scrap</div>
                  <div class="detail-value">${part.scrap_percentage || '0'}%</div>
                </div>
                <div class="detail-item">
                  <div class="detail-label">Cor</div>
                  <div class="detail-value">${part.color || '-'}</div>
                </div>
              </div>
              
              ${(part.weight_min || part.weight_max) ? `
                <div class="weight-info">
                  <h4>Controlo de Peso</h4>
                  <div class="part-details">
                    <div class="detail-item">
                      <div class="detail-label">Peso Mínimo</div>
                      <div class="detail-value">${part.weight_min ? part.weight_min + ' g' : '-'}</div>
                    </div>
                    <div class="detail-item">
                      <div class="detail-label">Peso Máximo</div>
                      <div class="detail-value">${part.weight_max ? part.weight_max + ' g' : '-'}</div>
                    </div>
                    <div class="detail-item">
                      <div class="detail-label">Tolerância</div>
                      <div class="detail-value">${part.weight_tolerance_pct || '5'}%</div>
                    </div>
                  </div>
                </div>
              ` : ''}
              
              ${part.quality_criteria ? `
                <div class="quality-criteria">
                  <h4>Critérios de Qualidade</h4>
                  <p>${part.quality_criteria}</p>
                </div>
              ` : ''}
            </div>
          `).join('')}
        </div>
        
        <div class="footer">
          <p>EP3D ENGINEERING PROJECT - Sistema de Gestão de Produção</p>
          <p>Documento gerado automaticamente</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight">
            Gestão de Peças
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure as peças produzidas na fábrica
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <ExcelExportButton endpoint="parts" label="Excel" />
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const r = await axios.get(`${API}/parts/export/csv`, { responseType: "blob" });
                const disp = r.headers["content-disposition"] || "";
                const match = disp.match(/filename="([^"]+)"/);
                const filename = match ? match[1] : `pecas-ep3d-${Date.now()}.csv`;
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
                const a = document.createElement("a");
                a.href = url; a.download = filename; a.click();
                window.URL.revokeObjectURL(url);
                toast.success(`CSV descarregado (${filename})`);
              } catch (e) {
                toast.error("Erro ao exportar CSV");
              }
            }}
            data-testid="export-parts-csv-btn"
            title="Exportar catálogo em CSV (Excel/Google Sheets/CRM/ERP)"
          >
            <FileText className="h-4 w-4 mr-2" />
            Exportar CSV (Vendas)
          </Button>
          <Button 
            variant="outline" 
            onClick={exportToPDF}
            data-testid="export-parts-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => handleOpenDialog()} data-testid="add-part-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nova Peça
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo">
                {editingPart ? "Editar Peça" : "Nova Peça"}
              </DialogTitle>
              <DialogDescription>
                Preencha os dados da peça
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Código *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="Ex: P001"
                    required
                    data-testid="part-code"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client">Cliente</Label>
                  <Input
                    id="client"
                    value={formData.client}
                    onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                    placeholder="Nome do cliente (opcional)"
                    data-testid="part-client"
                  />
                </div>
              </div>

              {/* Equipamento associado (Molde + Maquina) */}
              <div className="border-t pt-3 mt-2">
                <h4 className="font-medium mb-2 flex items-center gap-2 text-sm">
                  🏭 Equipamento Associado
                  <span className="text-xs text-muted-foreground font-normal">(usado pelo PCQ e Instruções de Trabalho)</span>
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mold_id">Molde</Label>
                    <select
                      id="mold_id"
                      value={formData.mold_id}
                      onChange={(e) => setFormData({ ...formData, mold_id: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="part-mold"
                    >
                      <option value="">-- Selecionar molde --</option>
                      {molds.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.code} - {m.description || ""} {m.cavities ? `(${m.cavities} cav)` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="machine_id">Máquina</Label>
                    <select
                      id="machine_id"
                      value={formData.machine_id}
                      onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="part-machine"
                    >
                      <option value="">-- Selecionar máquina --</option>
                      {machines.map(m => (
                        <option key={m.id} value={m.id}>
                          {m.code} {m.tonnage ? `(${m.tonnage}T)` : ""}{m.description ? ` - ${m.description}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição *</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição da peça"
                  required
                  data-testid="part-description"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="unit_weight">Peso (g) *</Label>
                  <Input
                    id="unit_weight"
                    type="number"
                    step="0.1"
                    value={formData.unit_weight}
                    onChange={(e) => setFormData({ ...formData, unit_weight: e.target.value })}
                    placeholder="25.5"
                    required
                    data-testid="part-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="plastic_type">Tipo de Plástico *</Label>
                  <select
                    id="plastic_type"
                    value={formData.plastic_type}
                    onChange={(e) => setFormData({ ...formData, plastic_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="part-plastic-type"
                  >
                    {PLASTIC_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product_type">Tipo de Produto (BOM) *</Label>
                  <select
                    id="product_type"
                    value={formData.product_type}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="part-product-type"
                  >
                    <option value="injected">Injetado (produzido)</option>
                    <option value="purchased">Comprado (fornecedor)</option>
                    <option value="subassembly">Subconjunto (montagem intermédia)</option>
                    <option value="assembled">Montado (produto final)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stock_quantity">Stock Actual (unidades)</Label>
                  <Input
                    id="stock_quantity"
                    type="number" step="0.01" min="0"
                    value={formData.stock_quantity}
                    onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
                    placeholder="0"
                    data-testid="part-stock-qty"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="min_stock_alert">Stock Mínimo (alerta)</Label>
                  <Input
                    id="min_stock_alert"
                    type="number" step="0.01" min="0"
                    value={formData.min_stock_alert}
                    onChange={(e) => setFormData({ ...formData, min_stock_alert: e.target.value })}
                    placeholder="0"
                  />
                </div>
                {formData.product_type === "purchased" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="supplier">Fornecedor</Label>
                      <Input id="supplier" value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} placeholder="Nome do fornecedor" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplier_price">Preço Unit. Fornecedor (€)</Label>
                      <Input id="supplier_price" type="number" step="0.001" min="0" value={formData.supplier_price} onChange={(e) => setFormData({ ...formData, supplier_price: e.target.value })} placeholder="0.00" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label htmlFor="scrap_percentage">Sucata Esperada (%)</Label>
                  <Input
                    id="scrap_percentage"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={formData.scrap_percentage}
                    onChange={(e) => setFormData({ ...formData, scrap_percentage: e.target.value })}
                    placeholder="5"
                    data-testid="part-scrap"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="quality_criteria">Critérios de Qualidade</Label>
                <Input
                  id="quality_criteria"
                  value={formData.quality_criteria}
                  onChange={(e) => setFormData({ ...formData, quality_criteria: e.target.value })}
                  placeholder="Separar por vírgula: Sem rebarbas, Cor uniforme"
                  data-testid="part-quality"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="image_url">Imagem / Desenho da Peça</Label>
                <div className="flex items-start gap-3">
                  {formData.image_url ? (
                    <div className="relative shrink-0">
                      <img src={formData.image_url} alt="Peça" className="h-24 w-24 object-cover rounded border-2 border-slate-200" />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: "" })}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs shadow"
                        title="Remover imagem"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="h-24 w-24 bg-slate-100 rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs text-center shrink-0">
                      Sem imagem
                    </div>
                  )}
                  <div className="flex-1 space-y-2">
                    <label className="block">
                      <input
                        id="image_url"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 3 * 1024 * 1024) { toast.error("Imagem muito grande (max 3MB)"); return; }
                          const reader = new FileReader();
                          reader.onload = () => setFormData({ ...formData, image_url: reader.result });
                          reader.readAsDataURL(file);
                        }}
                        className="hidden"
                        data-testid="part-image-file"
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded cursor-pointer hover:bg-slate-50">
                        📷 {formData.image_url ? "Trocar imagem" : "Carregar imagem"}
                      </span>
                    </label>
                    <p className="text-[10px] text-slate-500">JPG/PNG/WEBP até 3MB · aceita foto da câmara</p>
                  </div>
                </div>
              </div>
              
              {/* Weight Control Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  ⚖️ Controlo de Peso
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Defina limites de peso para alertas automáticos durante pesagens. Se não definir min/max, será usada a tolerância em %.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight_min">Peso Mínimo (g)</Label>
                    <Input
                      id="weight_min"
                      type="number"
                      step="0.01"
                      value={formData.weight_min}
                      onChange={(e) => setFormData({ ...formData, weight_min: e.target.value })}
                      placeholder={formData.unit_weight ? (parseFloat(formData.unit_weight) * 0.95).toFixed(2) : "Auto"}
                      data-testid="part-weight-min"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight_max">Peso Máximo (g)</Label>
                    <Input
                      id="weight_max"
                      type="number"
                      step="0.01"
                      value={formData.weight_max}
                      onChange={(e) => setFormData({ ...formData, weight_max: e.target.value })}
                      placeholder={formData.unit_weight ? (parseFloat(formData.unit_weight) * 1.05).toFixed(2) : "Auto"}
                      data-testid="part-weight-max"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight_tolerance_pct">Tolerância (%)</Label>
                    <Input
                      id="weight_tolerance_pct"
                      type="number"
                      step="0.1"
                      min="0"
                      max="50"
                      value={formData.weight_tolerance_pct}
                      onChange={(e) => setFormData({ ...formData, weight_tolerance_pct: e.target.value })}
                      placeholder="5"
                      data-testid="part-weight-tolerance"
                    />
                  </div>
                </div>
              </div>

              {/* Packaging Criteria Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  📦 Critério de Embalagem
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Define como a peça é embalada. Usado para gerar a Folha de Embalagem em cada Ordem de Fabrico.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="weight_10_pieces">Peso 10 peças (g)</Label>
                    <Input
                      id="weight_10_pieces"
                      type="number"
                      step="0.01"
                      value={formData.weight_10_pieces}
                      onChange={(e) => setFormData({ ...formData, weight_10_pieces: e.target.value })}
                      placeholder={formData.unit_weight ? (parseFloat(formData.unit_weight) * 10).toFixed(2) : "Auto"}
                      data-testid="part-weight-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="units_per_bag">Unidades por saco</Label>
                    <Input
                      id="units_per_bag"
                      type="number"
                      min="1"
                      value={formData.units_per_bag}
                      onChange={(e) => setFormData({ ...formData, units_per_bag: e.target.value })}
                      placeholder="250"
                      data-testid="part-units-per-bag"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="empty_bag_weight">Peso saco vazio (g)</Label>
                    <Input
                      id="empty_bag_weight"
                      type="number"
                      step="0.1"
                      min="0"
                      value={formData.empty_bag_weight}
                      onChange={(e) => setFormData({ ...formData, empty_bag_weight: e.target.value })}
                      placeholder="15"
                      data-testid="part-empty-bag-weight"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bags_per_box">Sacos por caixa</Label>
                    <Input
                      id="bags_per_box"
                      type="number"
                      min="1"
                      value={formData.bags_per_box}
                      onChange={(e) => setFormData({ ...formData, bags_per_box: e.target.value })}
                      placeholder="12"
                      data-testid="part-bags-per-box"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="boxes_per_pallet">Caixas por palete</Label>
                    <Input
                      id="boxes_per_pallet"
                      type="number"
                      min="0"
                      value={formData.boxes_per_pallet}
                      onChange={(e) => setFormData({ ...formData, boxes_per_pallet: e.target.value })}
                      placeholder="0 = não definido"
                      data-testid="part-boxes-per-pallet"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="box_dimensions">Dimensões caixa (cm)</Label>
                    <Input
                      id="box_dimensions"
                      value={formData.box_dimensions}
                      onChange={(e) => setFormData({ ...formData, box_dimensions: e.target.value })}
                      placeholder="40 x 30 x 20"
                      data-testid="part-box-dimensions"
                    />
                  </div>
                </div>
                {/* Live preview */}
                {formData.unit_weight && formData.units_per_bag && formData.bags_per_box && (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
                    <div className="font-bold text-slate-700 mb-1">📊 Pré-visualização (calculado):</div>
                    <div>• Quantidade por caixa: <strong>{(parseInt(formData.units_per_bag) || 0) * (parseInt(formData.bags_per_box) || 0)} un</strong></div>
                    <div>• Peso saco (sem saco): <strong>{((parseFloat(formData.unit_weight) || 0) * (parseInt(formData.units_per_bag) || 0)).toFixed(1)} g</strong></div>
                    <div>• Peso saco (com saco): <strong>{((parseFloat(formData.unit_weight) || 0) * (parseInt(formData.units_per_bag) || 0) + (parseFloat(formData.empty_bag_weight) || 0)).toFixed(1)} g</strong></div>
                    <div>• Peso bruto caixa: <strong>{(((parseFloat(formData.unit_weight) || 0) * (parseInt(formData.units_per_bag) || 0) + (parseFloat(formData.empty_bag_weight) || 0)) * (parseInt(formData.bags_per_box) || 0) / 1000).toFixed(2)} kg</strong></div>
                    {formData.boxes_per_pallet && parseInt(formData.boxes_per_pallet) > 0 && (
                      <div>• Quantidade por palete: <strong>{(parseInt(formData.units_per_bag) || 0) * (parseInt(formData.bags_per_box) || 0) * (parseInt(formData.boxes_per_pallet) || 0)} un</strong></div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-part-btn">
                  {editingPart ? "Guardar Alterações" : "Criar Peça"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Table */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <Box className="h-5 w-5" />
            Lista de Peças ({parts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {parts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Peso (g)</TableHead>
                    <TableHead>Plástico</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead>Sucata</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parts.map((part) => {
                    const ptype = part.product_type || "injected";
                    const typeColors = { injected: "bg-blue-100 text-blue-700", purchased: "bg-amber-100 text-amber-700", assembled: "bg-emerald-100 text-emerald-700", subassembly: "bg-indigo-100 text-indigo-700" };
                    const typeLabels = { injected: "Injetado", purchased: "Comprado", assembled: "Montado", subassembly: "Subconj." };
                    const stock = part.stock_quantity ?? 0;
                    const belowMin = part.min_stock_alert && stock < part.min_stock_alert;
                    return (
                    <TableRow key={part.id} data-testid={`part-row-${part.code}`}>
                      <TableCell className="font-mono font-bold">{part.code}</TableCell>
                      <TableCell>{part.description}</TableCell>
                      <TableCell><span className={`text-[10px] px-2 py-0.5 rounded font-bold ${typeColors[ptype]}`}>{typeLabels[ptype]}</span></TableCell>
                      <TableCell className="font-mono">{part.unit_weight}</TableCell>
                      <TableCell>{part.plastic_type}</TableCell>
                      <TableCell className={`text-right font-mono font-bold ${belowMin ? "text-rose-600" : ""}`}>
                        {stock}
                        {belowMin && <span className="ml-1 text-[10px] font-normal">⚠</span>}
                      </TableCell>
                      <TableCell className="font-mono">{((part.scrap_percentage ?? 0) * 100).toFixed(1)}%</TableCell>
                      <TableCell>{part.client}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(part)}
                            data-testid={`edit-part-${part.code}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(part.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-part-${part.code}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Box className="h-16 w-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg">Nenhuma peça registada</p>
              <p className="text-sm">Clique em "Nova Peça" para começar</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
